import { MouseEventHandler, MouseEvent, ReactNode, forwardRef, MutableRefObject, Ref } from 'react'
import * as styles from 'components/features/pfm/Button/Button.css'
import themes from 'styles/base/themes.styl'
import * as cn from 'classnames'
import Icon from 'components/atoms/Icon'
import { QAMarked, ShadowKind, Theme, ThemeStyle } from 'interfaces/component'
import { getSvgByFilename } from 'helpers/mediaHelpers'
import { getNamedChildrenRenderer, getChildWithType } from 'tangle-shared'
import SvgIcon from 'components/atoms/SvgIcon'
import ChevronSvg from 'icons/chevron.svg'
import CheckmarkSvg from 'icons/checkmark.svg'
import { AnalyticsEvent } from 'interfaces/analytics'
import { trackUIEventAction } from 'actions/analyticsActions'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'

export type ButtonSize =
  | 'micro'
  | 'mini'
  | 'tiny'
  | 'small'
  | 'medium'
  | 'large'
  | 'responsive'
  | 'square'

export type ButtonTheme =
  | Theme
  | 'textOnly' // Deprecated - use ButtonStyle .textOnly

export type ButtonStyle =
  | ThemeStyle
  | 'asText'
  | 'textOnlyBig'
  | 'responsiveTextFilled'

export type ButtonProps =
  & QAMarked
  & ButtonStyleProps
  & {
    children?: ReactNode
    disabled?: boolean
    submit?: boolean
    isSubmitting?: boolean
    text?: string
    onClick?: MouseEventHandler<HTMLButtonElement>
    analyticsEvent?: AnalyticsEvent
  }

export type ButtonStyleProps = {
  size?: ButtonSize
  theme?: ButtonTheme
  style?: ButtonStyle
  fullWidth?: boolean
  fullHeight?: boolean
  wrapText?: boolean
  pushUp?: boolean
  isSubmitting?: boolean
  shadow?: ShadowKind
  iconSlug?: string
  title?: string
  withChevron?: boolean
  submittingChildrenVisible?: boolean
  className?: string
  innerRef?: Ref<HTMLButtonElement>
}

export const getButtonClassName = (props: ButtonStyleProps) => {
  const {
    fullWidth,
    fullHeight,
    isSubmitting,
    size = 'medium',
    theme = 'blue',
    wrapText,
    pushUp,
    shadow,
    submittingChildrenVisible,
    style,
  } = props

  return cn(styles.button, size ? styles[size] : undefined,
    theme ? themes[theme as keyof typeof themes] : undefined,
    themes.clickable, {
      [styles.submittingChildrenVisible]: submittingChildrenVisible,

      [styles.fullWidth]: fullWidth,
      [styles.fullHeight]: fullHeight,
      [styles.wrapText]: wrapText,
      [styles.pushUp]: pushUp,
      [styles.isSubmitting]: isSubmitting,

      [styles.lightShadow]: shadow === 'light',
      [styles.heavyShadow]: shadow === 'heavy',
      [styles.blueShadow]: shadow === 'blue',

      [themes.hollow]: style === 'hollow',
      [themes.outlined]: style === 'outlined',
      [themes.textOnly]: !!style && ['textOnly', 'textOnlyBig', 'asText'].includes(style),

      // Theme color modifiers
      [styles.asText]: style === 'asText',
      [styles.textOnly]: style === 'textOnly',
      [styles.textOnlyBig]: style === 'textOnlyBig',
      [styles.responsiveTextFilled]: style === 'responsiveTextFilled',
    })
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, forwardedRef) => {
  const {
    children,
    text,
    title,
    disabled,
    submit,
    qaMarker,
    qaType,
    isSubmitting,
    onClick,
    iconSlug,
    withChevron,
    theme = 'blue',
    innerRef,
    analyticsEvent,
    className: outerClassName,
  } = props

  const buttonType = submit ? 'submit' : 'button'
  const styleClassNames = getButtonClassName(props)
  const className = cn(styleClassNames, {
    [styles.iconOnly]: !text && !children && !!iconSlug,
  })


  const iconChild = iconSlug && (
    <i className={ styles.icon }>
      <SvgIcon src={ getSvgByFilename(iconSlug) } />
    </i>
  )

  const titleChild = title && (
    <i className={ styles.title }>
      { title }
    </i>
  )

  const textChild = text && (
    <div className={ styles.buttonText }>
      { text }
    </div>
  )

  const chevronChild = withChevron && (
    <div className={ styles.chevron }>
      <SvgIcon src={ ChevronSvg } />
    </div>
  )

  const checkMarkChild = theme !== 'advisorTopicSelected' ? null : (
    <div className={ styles.checkmark }>
      <div className={ styles.checkmarkIcon }>
        <SvgIcon src={ CheckmarkSvg } />
      </div>
    </div>
  )

  const body = (
    <>
      { iconChild }
      { titleChild }
      { textChild }
      { chevronChild }
      { checkMarkChild }
    </>
  )

  const child = children || body

  const defaultContent = (
    <span>
      <span className={ styles.buttonContent }>
        { isSubmitting ? (
          <>
            <span className={ styles.submittingContent }>
              { child }
            </span>
            <div className={ styles.loadingIconWrapper }>
              <Icon kind='spinner-light' />
            </div>
          </>
        ) : (
          child
        )}
      </span>
    </span>
  )

  const usedUncontrolledContent = getNamedChildrenRenderer('UncontrolledContent')
  const uncontrolledContent = getChildWithType(children, usedUncontrolledContent)
  const isDisabled = disabled || isSubmitting

  const dispatch = useStoreDispatch()

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick && onClick(event)
    !!analyticsEvent && dispatch(trackUIEventAction(analyticsEvent))
  }

  const setRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
    if (!ref) {
      return
    }

    if (typeof ref === 'function') {
      ref(value)
      return
    }

    (ref as MutableRefObject<T | null>).current = value
  }

  const setButtonRef = (node: HTMLButtonElement | null) => {
    setRef(forwardedRef, node)
    setRef(innerRef, node)
  }

  return (
    <button
      data-qa-marker={ qaMarker }
      data-qa-type={ qaType }
      className={ cn(className, outerClassName) }
      disabled={ isDisabled }
      type={ buttonType }
      onClick={ isDisabled ? undefined : handleClick }
      ref={ setButtonRef }
    >
      { uncontrolledContent || defaultContent }
    </button>
  )
})

Button.displayName = 'Button'

export default Button
